import React, { useRef, useEffect, useState, useCallback } from 'react';
import { analyticsEvents } from '../utils/analytics';
import { useAudioManager } from '../utils/audioManager';
import { calculateHalfOctaveBandwidth } from '../hooks/useNotchedWhiteNoise';
import '../styles/MobileSliders.css';

interface NotchFilterVisualizationProps {
  className?: string;
}

export const NotchFilterVisualization: React.FC<NotchFilterVisualizationProps> = ({ className = '' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationIdRef = useRef<number | undefined>(undefined);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const noiseSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const notchFilterRef = useRef<BiquadFilterNode | null>(null);
  const isPlayingRef = useRef(false);
  const frequencyRef = useRef(1000);

  const [isPlaying, setIsPlaying] = useState(false);
  const [frequency, setFrequency] = useState(1000);
  const [volume, setVolume] = useState(0.2);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Calculate bandwidth automatically based on research
  const bandwidth = calculateHalfOctaveBandwidth(frequency);
  
  // Calculate percentage for progress fill
  const frequencyPercentage = ((frequency - 100) / (8000 - 100)) * 100;
  const volumePercentage = (volume / 0.5) * 100;

  // Use audio manager to prevent conflicts
  const { isBlocked, requestControl, releaseControl, currentSource } = useAudioManager('visualization');

  // Generate white noise buffer
  const generateWhiteNoise = useCallback((audioContext: AudioContext, duration = 2): AudioBuffer => {
    const sampleRate = audioContext.sampleRate;
    const bufferLength = sampleRate * duration;
    const buffer = audioContext.createBuffer(2, bufferLength, sampleRate);

    for (let channel = 0; channel < 2; channel++) {
      const channelData = buffer.getChannelData(channel);
      for (let i = 0; i < bufferLength; i++) {
        channelData[i] = Math.random() * 2 - 1;
      }
    }
    return buffer;
  }, []);

  // Draw spectrum visualization
  const drawSpectrum = useCallback(() => {
    if (!canvasRef.current || !analyserRef.current || !audioContextRef.current || !isPlayingRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyser.getByteFrequencyData(dataArray);
    
    // Debug: Check if we're getting data
    let maxValue = 0;
    for (let i = 0; i < Math.min(dataArray.length, 100); i++) {
      if (dataArray[i] > maxValue) maxValue = dataArray[i];
    }
    

    // Set canvas size with device pixel ratio
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    
    // Reset canvas size if needed
    if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
      canvas.width = width * dpr;
      canvas.height = height * dpr;
    }
    
    // Clear and setup context
    ctx.save();
    ctx.scale(dpr, dpr);
    
    // Clear canvas with light background
    ctx.fillStyle = '#f8f9fa'; // medical-gray-50
    ctx.fillRect(0, 0, width, height);
    
    // Draw grid lines
    ctx.strokeStyle = 'rgba(0, 86, 179, 0.1)'; // medical-blue-500 with opacity
    ctx.lineWidth = 1;
    
    // Horizontal grid lines
    for (let i = 0; i <= 5; i++) {
      const y = (height / 5) * i;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Calculate frequency range we want to display (up to 8kHz)
    const maxFreq = 8000;
    const nyquist = audioContextRef.current.sampleRate / 2;
    const maxBin = Math.floor((maxFreq / nyquist) * bufferLength);
    
    // Calculate bar dimensions
    const barWidth = width / maxBin;
    let x = 0;

    // Draw spectrum bars
    for (let i = 0; i < maxBin; i++) {
      const value = dataArray[i];
      const barHeight = (value / 255) * height * 0.8;
      const freq = (i * nyquist) / bufferLength;

      // Color based on whether frequency is in notch region
      const currentBandwidth = calculateHalfOctaveBandwidth(frequencyRef.current);
      if (freq >= frequencyRef.current - currentBandwidth / 2 && freq <= frequencyRef.current + currentBandwidth / 2) {
        ctx.fillStyle = '#ec4899'; // pink-500 for notched region
      } else {
        ctx.fillStyle = '#a855f7'; // purple-500
      }

      // Draw bar if it has a value
      if (value > 0) {
        ctx.fillRect(x, height - barHeight - 20, Math.max(barWidth - 1, 1), barHeight);
      }
      x += barWidth;
    }
    
    // If no data, draw a test pattern to verify canvas is working
    if (maxValue === 0) {
      ctx.fillStyle = 'rgba(14, 165, 233, 0.2)'; // light blue
      ctx.fillRect(0, height/2, width, 2);
      ctx.fillStyle = '#dc2626';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Waiting for audio data...', width/2, height/2);
    }

    // Draw frequency markers
    ctx.fillStyle = '#6c757d'; // medical-gray-500
    ctx.font = '11px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'center';

    // Show fewer markers on mobile
    const isMobile = width < 640;
    const frequencyMarkers = isMobile ? [500, 1000, 5000] : [100, 500, 1000, 2000, 5000, 8000];
    frequencyMarkers.forEach(markerFreq => {
      if (markerFreq <= maxFreq) {
        const x = (markerFreq / maxFreq) * width;
        const label = markerFreq >= 1000 ? `${markerFreq / 1000}kHz` : `${markerFreq}Hz`;
        ctx.fillText(label, x, height - 5);
        
        // Draw vertical grid line
        ctx.strokeStyle = 'rgba(0, 86, 179, 0.05)';
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height - 20);
        ctx.stroke();
      }
    });

    // Draw notch frequency indicator
    const notchX = (frequencyRef.current / maxFreq) * width;
    ctx.strokeStyle = '#ec4899'; // pink-500
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(notchX, 0);
    ctx.lineTo(notchX, height - 20);
    ctx.stroke();
    ctx.setLineDash([]);
    
    // Draw notch frequency label
    ctx.fillStyle = '#ec4899';
    ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
    ctx.fillText(`${frequencyRef.current}Hz (${calculateHalfOctaveBandwidth(frequencyRef.current)}Hz)`, notchX, 15);
    
    // Restore context
    ctx.restore();

    // Continue animation
    animationIdRef.current = requestAnimationFrame(drawSpectrum);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start audio
  const handlePlay = useCallback(async () => {
    // Check if we can get audio control
    if (!requestControl()) {
      // Another audio source is playing
      analyticsEvents.trackVisualizationInteraction('blocked', frequency);
      return;
    }

    setIsLoading(true);
    
    try {
      // Create or resume audio context
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }

      const audioContext = audioContextRef.current;
      
      // Resume if suspended
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      // Create audio nodes
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;
      analyser.minDecibels = -90;
      analyser.maxDecibels = -10;
      analyserRef.current = analyser;

      const noiseBuffer = generateWhiteNoise(audioContext);
      const noiseSource = audioContext.createBufferSource();
      const gainNode = audioContext.createGain();
      const notchFilter = audioContext.createBiquadFilter();

      // Configure notch filter
      notchFilter.type = 'notch';
      notchFilter.frequency.value = frequency;
      notchFilter.Q.value = frequency / bandwidth;

      // Set buffer and loop
      noiseSource.buffer = noiseBuffer;
      noiseSource.loop = true;
      
      // Connect audio graph - connect to both analyser and destination
      noiseSource.connect(notchFilter);
      notchFilter.connect(gainNode);
      
      // Split the signal - one to analyser, one to speakers
      gainNode.connect(analyser);
      gainNode.connect(audioContext.destination);
      
      // Set volume with smooth ramp
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 0.1);
      
      console.log('Audio graph connected:', {
        noiseSource: !!noiseSource,
        notchFilter: !!notchFilter,
        gainNode: !!gainNode,
        analyser: !!analyser,
        analyserChannelCount: analyser.channelCount,
        fftSize: analyser.fftSize
      });

      // Store references
      noiseSourceRef.current = noiseSource;
      gainNodeRef.current = gainNode;
      notchFilterRef.current = notchFilter;

      // Start playing
      noiseSource.start(0);
      setIsPlaying(true);
      isPlayingRef.current = true;
      setIsLoading(false);

      // Start visualization after a short delay to ensure audio is flowing
      setTimeout(() => {
        drawSpectrum();
      }, 100);

      // Track analytics
      analyticsEvents.trackVisualizationInteraction('play', frequency);
      
      console.log('Audio started:', {
        context: audioContext.state,
        sampleRate: audioContext.sampleRate,
        frequency: frequency,
        bandwidth: bandwidth
      });
    } catch (error) {
      console.error('Error starting audio:', error);
      setIsLoading(false);
      analyticsEvents.trackError('Notch filter visualization play error');
      releaseControl();
    }
  }, [frequency, bandwidth, volume, generateWhiteNoise, drawSpectrum, requestControl, releaseControl]);

  // Stop audio
  const handleStop = useCallback(() => {
    if (noiseSourceRef.current) {
      noiseSourceRef.current.stop();
      noiseSourceRef.current = null;
    }

    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
    }

    setIsPlaying(false);
    isPlayingRef.current = false;
    releaseControl();

    // Track analytics
    analyticsEvents.trackVisualizationInteraction('stop', frequency);
  }, [frequency, releaseControl]);

  // Update filter parameters in real-time
  useEffect(() => {
    if (notchFilterRef.current && isPlaying) {
      notchFilterRef.current.frequency.value = frequency;
      notchFilterRef.current.Q.value = frequency / bandwidth;
    }
  }, [frequency, bandwidth, isPlaying]);

  // Update volume in real-time
  useEffect(() => {
    if (gainNodeRef.current && isPlaying) {
      gainNodeRef.current.gain.linearRampToValueAtTime(
        volume,
        audioContextRef.current!.currentTime + 0.1
      );
    }
  }, [volume, isPlaying]);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Stop any playing audio
      if (noiseSourceRef.current) {
        noiseSourceRef.current.stop();
        noiseSourceRef.current = null;
      }
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      // Close audio context
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      // Release audio control
      releaseControl();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on unmount

  return (
    <div className={`bg-white rounded-lg shadow-medical p-6 ${className}`}>
      {/* Canvas */}
      <div className="mb-6 border border-medical-gray-200 rounded-lg overflow-hidden">
        <canvas
          ref={canvasRef}
          className="w-full h-48 sm:h-56 md:h-64 bg-medical-gray-50"
          style={{ imageRendering: 'auto' }}
        />
      </div>

      {/* Controls */}
      <div className="space-y-4">
        {/* Frequency Slider */}
        <div>
          <label className="slider-label">
            <span>Notch Frequency</span>
            <span className="slider-value">{frequency} Hz</span>
          </label>
          <div className="mobile-slider-container">
            <div className="mobile-slider-track" />
            <div 
              className="mobile-slider-fill"
              style={{ width: `${frequencyPercentage}%` }}
            />
            <input
              type="range"
              min="100"
              max="8000"
              step="10"
              value={frequency}
              onChange={(e) => {
                const newFreq = Number(e.target.value);
                setFrequency(newFreq);
                frequencyRef.current = newFreq;
              }}
              className={isMobile ? "mobile-slider" : "mobile-slider compact-slider"}
              aria-label="Notch frequency adjustment"
            />
          </div>
        </div>

        {/* Bandwidth Display (Read-only) */}
        <div>
          <label className="flex items-center justify-between text-sm font-medium text-gray-700 mb-2">
            <span>Research-Optimized Bandwidth</span>
            <span className="text-purple-600 font-semibold">{bandwidth} Hz</span>
          </label>
          <div className="w-full h-2 bg-gray-200 rounded-lg relative overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg transition-all duration-300"
              style={{ width: `${Math.min(100, (bandwidth / 2000) * 100)}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Auto-calculated: 0.5 octave • Q Factor: {(frequency / bandwidth).toFixed(2)}
          </p>
        </div>

        {/* Volume Control */}
        <div>
          <label className="slider-label">
            <span>Volume</span>
            <span className="slider-value">{Math.round(volume * 100)}%</span>
          </label>
          <div className="mobile-slider-container">
            <div className="mobile-slider-track" />
            <div 
              className="mobile-slider-fill volume-slider-fill"
              style={{ width: `${volumePercentage}%` }}
            />
            <input
              type="range"
              min="0"
              max="0.5"
              step="0.05"
              value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
              className={isMobile ? "mobile-slider volume-slider" : "mobile-slider volume-slider compact-slider"}
              aria-label="Volume adjustment"
            />
          </div>
        </div>

        {/* Play/Stop Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            onClick={handlePlay}
            disabled={isPlaying || isLoading}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
              isPlaying || isLoading
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-purple-500 text-white hover:bg-purple-600 active:scale-95'
            }`}
          >
            {isLoading ? 'Loading...' : 'Play Notched White Noise'}
          </button>
          <button
            onClick={handleStop}
            disabled={!isPlaying}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
              !isPlaying
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-red-500 text-white hover:bg-red-600 active:scale-95'
            }`}
          >
            Stop
          </button>
        </div>
      </div>

      {/* Audio Conflict Warning */}
      {isBlocked && currentSource && (
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <span className="font-semibold">Audio in use:</span> Another audio source is currently playing. 
            Please stop the {currentSource === 'frequency-detection' ? 'frequency detection' : 
                          currentSource === 'notched-noise' ? 'notched noise therapy' : 'other audio'} 
            before using this visualization.
          </p>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-100">
        <h4 className="font-semibold text-purple-700 mb-2">Research-Based Visualization</h4>
        <p className="text-sm text-gray-700 mb-3">
          The <span className="text-pink-600 font-semibold">pink area</span> shows where frequencies are being filtered out using a 
          <strong> research-optimized 0.5 octave bandwidth</strong>. The bandwidth automatically adjusts based on 
          your selected frequency using clinically-validated calculations.
        </p>
        <p className="text-sm text-gray-700">
          Adjust only the frequency slider to match your tinnitus tone - the bandwidth is automatically 
          optimized for maximum therapeutic effectiveness based on scientific studies.
        </p>
      </div>

    </div>
  );
};